import { useEffect, useState } from 'react';

import { lowerCase, snakeCase } from 'lodash';
import { Loader2, Plus, Trash } from 'lucide-react';
import { Button, ButtonIcon, Col, DefaultPopover, DefaultTable, FormGroup, Input, Label, Row, Select, SheetFooter } from 'twyne-new.ui';

import { useConsentDBAsContext } from '../Context';
import { useConsentDBAsStore } from '../store';

export default function DefaultDBATab({ data, onChange, children, disabled }) {
  const { modal: { toggle } } = useConsentDBAsContext()
  const { collections: { useLoad, items: dbas }, collection: { save } } = useConsentDBAsStore()

  const [fields, setFields] = useState([])
  const [options, setOptions] = useState([])

  const loadDBAs = useLoad()

  useEffect(() => {
    if (data?.length && !fields.length) {
      setFields(data)
    }
  }, [data, fields])

  useEffect(() => {
    if (dbas?.length) {
      const options = dbas.map(({ label, value }) => ({ label, value }))
      setOptions(options)
    }
  }, [dbas])

  const handleChange = fields => {
    setFields(fields)
    onChange(fields)
  }

  const handleAdd = option => {
    const { label: dba, value: bidid } = option
    fields.push({ dba, bidid })
    handleChange(fields)
  }

  const handleSave = async dba => {
    if (dba.isNew) {
      await save({ dba })
      await loadDBAs()
    }
  }

  return <>
    {children}
    <AddButton options={options} onAdd={handleAdd} onSave={handleSave} disabled={disabled} />
    <List fields={fields} disabled={disabled} onChange={handleChange} />
    <SheetFooter className='absolute bottom-0 w-full left-0 right-0'>
      <Button color='neutral' variant='ghost' onClick={toggle} disabled={disabled}>Cancel</Button>
      <Button color='accent' onClick={toggle} disabled={disabled}>Apply</Button>
    </SheetFooter>
  </>
}

const List = ({ fields, disabled, onChange }) => {
  const handleEdit = (index, value) => {
    fields[index] = { ...fields[index], ...value }
    onChange(fields)
  }

  const handleRemove = index => {
    fields.splice(index, 1)
    onChange(fields)
  }

  const tableData = fields.map((field, index) => ({ ...field, id: index }))
  const columns = [
    {
      dataField: 'dba',
      text: 'DBA',
      formatter: (dba, row) => <FormGroup>
        <Input
          required
          value={dba}
          onChange={event => {
            const dba = event.target.value
            handleEdit(row.id, { dba, bidid: parseValue(dba) })
          }}
          disabled={disabled}
        />
      </FormGroup>,
    },
    {
      dataField: 'bidid',
      text: 'Bidid',
      formatter: bidid => <FormGroup>
        <Input required value={bidid} disabled />
      </FormGroup>,
    },
    {
      dataField: '',
      text: '',
      className: 'w-8',
      formatter: (_, row) => <>
        <ButtonIcon
          disabled={disabled}
          content='Delete'
          onClick={() => handleRemove(row.id)}
        >
          <Trash size={16} />
        </ButtonIcon>
      </>,
    },
  ]

  return <DefaultTable
    data={tableData}
    columns={columns}
    noDataIndication={() => "No DBA's found"}
  />
}

export function AddButton({ options, onAdd, onSave, disabled }) {
  const [open, setOpen] = useState(false)
  const { loading } = useConsentDBAsStore()

  const [selected, setSelected] = useState(null)

  const handleAdd = async () => {
    if (selected) {
      await onSave(selected)
      onAdd(selected)
      setSelected(null)
      setOpen(false)
    }
  }

  const handleSelect = option => {
    setSelected(option)
  }

  const handleValidate = label => {
    if (!label || !typeof label === 'string' || !label.length) {
      return false
    }
    if (!/^[a-zA-Z0-9 _-]+$/.test(label)) {
      return false
    }
    return true
  }

  const handleCreate = label => {
    label = label.trim()
    const value = parseValue(label)
    return { label, value, isNew: true }
  }

  return <div className='w-full flex justify-end'>
    <DefaultPopover
      wrapperProps={{ open, onOpenChange: setOpen }}
      triggerProps={{ asChild: true }}
      trigger={<div className=''>
        <Button size='sm' color='accent' disabled={disabled}>
          <Plus size={14} />
          Add
        </Button>
      </div>}
    >
      <Row>
        <Col>
          <FormGroup>
            <Label>Select DBA</Label>
            <Select
              creatable
              value={selected}
              options={options}
              onChange={handleSelect}
              disabled={disabled}
              newOptionCreator={handleCreate}
              isValidNewOption={handleValidate}
            />
          </FormGroup>
        </Col>
        <Col>
          <Button size='sm' block color='accent' onClick={handleAdd} disabled={!selected || loading}>
            {loading ? <Loader2 size={14} /> : 'Add'}
          </Button>
        </Col>
      </Row>
    </DefaultPopover>
  </div>
}

const parseValue = value => {
  value = snakeCase(lowerCase(value?.trim() || ''))
  return value.startsWith('static_') ? value : `static_${value}`
}
